import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { FormProps } from '../../helpers';
import ListToggle from '../../list-toggle';
import { UiOption } from '@/lib/helpers';
import PublicServiceButton from '@/components/public-service-button';
import { Box, Stack } from '@mui/material';
import { PublicServiceMeta, PublicServiceModel } from '@/lib/models/public-service/public-service.model';
import useQueryHelper from '@/hooks/use-query-helper';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import MonitorSwitchV1 from './monitor-switch';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import {
  getPublicServiceOptions,
  PublicServiceConfig,
  PublicServiceOptionMeta,
} from '@/lib/models/policy-model-v1/public-services';
import { cloneDeep, difference } from 'lodash';
import useDrawer from '@/hooks/use-drawer.hook';

interface Props extends FormProps {
  value: PublicServiceConfig;
  onChange: (value: PublicServiceConfig) => void;
  orgPolicy?: PolicyModelV1;
  aclVariant?: 'allow' | 'block';
}

const useStyles = createUseStyles({
  psList: {
    '& label.MuiFormControlLabel-root': {
      alignItems: 'flex-start',
      marginRight: 0,
    },
    '& span.MuiFormControlLabel-label': {
      display: 'block',
      width: '100%',
    },
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      flexGrow: 1,
    },
  },
});

const getServiceDisabledTooltip = (isAllowList: boolean, orgAllowDisabled: boolean): string => {
  if (isAllowList) {
    if (orgAllowDisabled) {
      return 'This service is blocked by default policy and cannot be allowed.';
    }
    return 'This service is on the block list and cannot be allowed.';
  }

  return 'This service is on the allow list and cannot be blocked.';
};

const PublicServiceAclV1: FC<Props> = ({
  value,
  onChange,
  orgPolicy,
  aclVariant = 'allow',
  ...props
}) => {
  const styles = useStyles();
  const isAllowList = aclVariant === 'allow';

  const { openDrawer } = useDrawer();

  const handleView = useCallback(
    (_event: MouseEvent, { id }: PublicServiceModel) => {
      openDrawer('public-service', { id });
    },
    [openDrawer]
  );

  const query = useAppData<PublicServiceMeta[]>(AppDataQueryKey.PublicServices);
  const { data = [] } = query;
  const { showLoader } = useQueryHelper(query);
  const { enabled = [], disabled = [], monitored = [] } = value;

  const onListToggleChange = useCallback(
    (option: UiOption[]) => {
      const updated = cloneDeep(value);
      const updateKey = isAllowList ? 'enabled' : 'disabled';
      const alternateKey = isAllowList ? 'disabled' : 'enabled';
      updated[updateKey] = option.map(({ value }) => value);
      updated[alternateKey] = difference(updated[alternateKey], updated[updateKey]);
      const removedFromAllowIds = difference(enabled, updated.enabled);
      updated.monitored = difference(monitored, [...removedFromAllowIds, ...updated.disabled]);
      onChange(updated);
    },
    [value, onChange, isAllowList, enabled, monitored]
  );

  const onSwitchChange = useCallback(
    (serviceId: string) => {
      const updatedMonitored = monitored.includes(serviceId)
        ? monitored.filter((id) => id !== serviceId)
        : [...monitored, serviceId];

      const updatedPublicServiceConfig = {
        ...value,
        monitored: updatedMonitored,
      };

      onChange(updatedPublicServiceConfig);
    },
    [monitored, onChange, value]
  );

  const psOptions: UiOption[] = useMemo(() => {
    const policy = new PolicyModelV1({ publicServices: value });
    const { newPublicServices } = policy;

    return getPublicServiceOptions(data, aclVariant, policy, orgPolicy).map(({ value, meta }) => {
      const { publicService, monitoredByOrgPolicy, disabledByOrgPolicy, disabledByAlternateList } =
        meta as PublicServiceOptionMeta;
      const { id } = publicService;

      // you cannot add a service to the allow list if it's blocked by default policy
      const orgAllowDisabled = isAllowList && disabledByOrgPolicy;

      const disabled = disabledByAlternateList || orgAllowDisabled;
      const isUnacknowledged = newPublicServices.includes(id);
      const LabelEl = (
        <Tooltip
          title={getServiceDisabledTooltip(isAllowList, orgAllowDisabled)}
          disabled={!disabled}
        >
          <Stack direction="row" alignItems="center" width="100%" gap={1}>
            {disabled && <Icon name="lock" size="small" color="grey" />}
            <PublicServiceButton publicService={publicService} onInfoClick={handleView} noDetails />

            {isUnacknowledged && (
              <Tooltip title="New service, monitored until acknowledged. Move it to Allow or Block to acknowledge it.">
                <Icon name="monitor" size="small" color="grey" />
              </Tooltip>
            )}

            {isAllowList && enabled.includes(id) && (
              <MonitorSwitchV1
                value={monitored.includes(id)}
                onChange={() => onSwitchChange(id)}
                disabled={monitoredByOrgPolicy}
              />
            )}
          </Stack>
        </Tooltip>
      );

      return {
        label: LabelEl,
        value,
        meta: { keyword: publicService.name },
        disabled,
      };
    });
  }, [
    data,
    enabled,
    handleView,
    monitored,
    onSwitchChange,
    orgPolicy,
    isAllowList,
    aclVariant,
    value,
  ]);

  const rightTitle = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Icon name={isAllowList ? 'check' : 'block'} size="small" />
      {isAllowList ? 'Allowed Services' : 'Blocked Services'}
    </Stack>
  );

  return (
    <Box className={styles.psList}>
      <ListToggle
        {...props}
        value={isAllowList ? enabled : disabled}
        onChange={onListToggleChange}
        label="Services"
        leftTitle="Available Services"
        rightTitle={rightTitle}
        options={psOptions}
        labelChecks={true}
        loadState={showLoader ? 'loading' : 'loaded'}
      />
    </Box>
  );
};

export default PublicServiceAclV1;
